import { useState, useEffect, Suspense, lazy, useLayoutEffect } from 'react';
import {
  Route,
  Routes,
  useNavigate,
  matchRoutes,
  useLocation,
} from 'react-router-dom';
import { usersInstance } from './axios-settings';
import i18next from 'i18next';

import { GlobalStyle } from './components/styles/globalStyles';
import AuthContext from './context/authContext';
import userMeContext from './context/userMeContext';
import FlowiseChat from './components/Chatbot/Flowise';
import { useWeglot } from './hooks/useWeglot';
import {
  clearAuthCookies,
  clearUserCookies,
  getCookie,
  setAuthCookies,
  setUserCookies,
} from './authUtils';

const Auth = lazy(() => import('./components/Auth'));
const RemindPass = lazy(() => import('./components/RemindPass'));
const Register = lazy(() => import('./components/RegisterPass'));
const Layout = lazy(() => import('./components/Layout'));
const AccountSettings = lazy(
  () => import('./components/AccountSettings/AccountSettings')
);
const UserView = lazy(() => import('./components/UserView'));
const ComponentsPage = lazy(() => import('./components/ComponentsPage'));

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!getCookie('token'));
  const [incorrectEmailOrPassword, setIncorrectEmailOrPassword] =
    useState(false);
  const [userMe, setUserData] = useState({});
  const [apiVer, setApiVer] = useState('');
  const { updateSiteLanguage } = useWeglot();
  const navigate = useNavigate();
  const location = useLocation();

  // Global loading state management
  useEffect(() => {
    const spinner = document.getElementById('loading-spinner');
    if (spinner) {
      // Hide spinner by default when app loads
      spinner.classList.add('none');
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const spinner = document.getElementById('loading-spinner');
      if (spinner) {
        spinner.classList.remove('none');
      }

      usersInstance
        .get('me')
        .then((response) => {
          setUserData(response.data);
          setUserCookies(response.data);
          if (spinner) {
            spinner.classList.add('none');
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoggedIn(false);
          if (spinner) {
            spinner.classList.add('none');
          }
        });
    }

    if (getCookie('token')) {
      setIsLoggedIn(true);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    usersInstance
      .get('version')
      .then(({ data: { version } }) => {
        setApiVer(version);
      })
      .catch((err) => {
        console.error('Error fetching API version', err);
      });
  }, []);

  const login = (values) => {
    const spinner = document.getElementById('loading-spinner');
    if (spinner) {
      spinner.classList.remove('none');
    }

    usersInstance
      .post('token', values)
      .then((res) => {
        setAuthCookies({
          token: res.data.token,
          refresh_token: res.data.refresh_token,
          expires_at: res.data.expires_at,
        });
        setIsLoggedIn(true);
        if (spinner) {
          spinner.classList.add('none');
        }
      })
      .catch((err) => {
        setIncorrectEmailOrPassword(true);
        console.error(err.response);
        if (spinner) {
          spinner.classList.add('none');
        }
      });
  };

  const logout = () => {
    const spinner = document.getElementById('loading-spinner');
    if (spinner) {
      spinner.classList.remove('none');
    }

    clearAuthCookies();
    clearUserCookies();
    setIsLoggedIn(false);

    // Hide spinner after logout
    setTimeout(() => {
      if (spinner) {
        spinner.classList.add('none');
      }
    }, 100);
  };

  const unloggedRouter = [
    {
      path: '/register/:registerToken',
      element: <Register />,
    },
    {
      path: '/password-remind',
      element: <RemindPass />,
    },
    {
      path: '/users/password/:token',
      element: <Register resetingForm={true} />,
    },
    {
      path: '/',
      element: <Auth loginError={incorrectEmailOrPassword} />,
    },
  ];

  const loggedRouter = [
    {
      path: '/components',
      element: <ComponentsPage />,
    },
    {
      path: '/account-settings',
      element: <AccountSettings user={userMe} />,
    },
    {
      path: '/admin/*',
      exact: true,
      element: <Layout user={userMe} />,
    },
    {
      path: '/*',
      element: <UserView user={userMe} />,
    },
  ];

  const publicRoutes = [
    { path: '/register/:registerToken' },
    { path: '/password-remind' },
    { path: '/users/password/:token' },
  ];

  function isPublicRoute(pathname) {
    return !!matchRoutes(publicRoutes, { pathname });
  }

  /* Reload Weglot */
  useEffect(() => {
    if (userMe.language) {
      const spinner = document.getElementById('loading-spinner');
      if (spinner) {
        spinner.classList.remove('none');
      }

      updateSiteLanguage(userMe.language);

      // Hide spinner after language update
      setTimeout(() => {
        if (spinner) {
          spinner.classList.add('none');
        }
      }, 100);
    }
  }, [userMe.language]);

  useEffect(() => {
    if (!isLoggedIn && !isPublicRoute(location.pathname)) {
      const spinner = document.getElementById('loading-spinner');
      if (spinner) {
        spinner.classList.remove('none');
      }
      navigate('/');
      // Hide spinner after navigation
      setTimeout(() => {
        if (spinner) {
          spinner.classList.add('none');
        }
      }, 100);
    }
  }, [isLoggedIn, location.pathname, navigate]);

  useEffect(() => {
    window.addEventListener('error', (e) => {
      if (
        e.message ===
        'ResizeObserver loop completed with undelivered notifications.'
      ) {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    });
  }, []);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <>
      <GlobalStyle />
      <Suspense fallback={<h1>{i18next.t('loadingView')}...</h1>}>
        <userMeContext.Provider value={{ userMe }}>
          <AuthContext.Provider value={{ isLoggedIn, login, logout }}>
            <Routes>
              {(!isLoggedIn ? unloggedRouter : loggedRouter).map((route) => (
                <Route
                  path={route.path}
                  element={route.element}
                  key={route.path}
                  exact={route.exact}
                />
              ))}
            </Routes>

            {isLoggedIn && <FlowiseChat />}
          </AuthContext.Provider>
        </userMeContext.Provider>
      </Suspense>
      <div style={{ fontSize: '0.4rem', textAlign: 'center' }}>
        <p className="do-not-translate">API version: {apiVer}</p>
        <p className="do-not-translate">
          Client version: {process.env.REACT_APP_VERSION}
        </p>
        <p
          id="server_app_version"
          className="do-not-translate server_app_version"
          style={{ display: 'none' }}
        >
          {apiVer}
        </p>
        <p
          id="client_app_version"
          className="do-not-translate client_app_version"
          style={{ display: 'none' }}
        >
          {process.env.REACT_APP_VERSION}
        </p>
        <p
          id="client_instance"
          className="do-not-translate client_instance"
          style={{ display: 'none' }}
        >
          {process.env.REACT_APP_CLIENT_INSTANCE}
        </p>
      </div>
    </>
  );
};

export default App;
